(ns app.front.view.application-wizard.plan
  (:require [app.domain.municipality.model :as municipality-model]
            [app.domain.project.model :as project-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.file.upload :as upload]
            [app.front.ui :as ui]
            [app.front.ui.icon-button :as ui-ib]
            [app.front.ui.tabs :as ui-tabs]
            [app.front.util :as futil]
            [app.front.view.application-wizard.authority-create :as authority-create]
            [app.front.view.application-wizard.classification :as classification]
            [app.front.view.application-wizard.location :as location]
            [app.front.view.application-wizard.subs :as subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.front.routing :as routing]
            [core.util.string :as str-util]
            [potpuri.core :as p]
            [re-chain.core :as rc]
            [re-frame.core-instrumented :as rf]))


;; State

; Set if an existing project was chosen. Has priority over project-name.
(rf/reg-sub ::project-id :-> ::project-id)
(rf/reg-event-db ::select-project
  (fn [db [_ id]] (assoc db ::project-id id)))

; UI-facing variants that keep the `project` URL query param in sync with the
; selection, so a refresh doesn't revert to the project passed in on entry.
(rf/reg-event-fx ::select-project-from-ui
  (fn [{:keys [db]} [_ id]]
    {:db       (assoc db ::project-id id)
     :dispatch [::routing/merge-query-params {:project id} :route/replace]}))
(rf/reg-event-fx ::clear-selected-project-from-ui
  (fn [{:keys [db]} _]
    {:db       (dissoc db ::project-id)
     :dispatch [::routing/merge-query-params {:project nil} :route/replace]}))

; New project's name.
(rf/reg-sub ::project-name :-> ::project-name)
(rf/reg-event-db ::set-project-name
  (fn [db [_ value]] (assoc db ::project-name value)))

; Description of the work being done in this application.
(rf/reg-sub ::description-of-work :-> ::description-of-work)
(rf/reg-event-db ::set-description-of-work
  (fn [db [_ value]] (assoc db ::description-of-work value)))

; UI-facing variants that keep the plan URL query params in sync, so a refresh
; doesn't discard the values entered in the form.
(rf/reg-event-fx ::set-project-name-from-ui
  (fn [{:keys [db]} [_ value]]
    (let [value (str-util/blank->nil value)]
      {:db       (p/assoc-if (dissoc db ::project-name) ::project-name value)
       :dispatch [::routing/merge-query-params {:project-name value} :route/replace]})))

(rf/reg-event-fx ::set-description-of-work-from-ui
  (fn [{:keys [db]} [_ value]]
    (let [value (str-util/blank->nil value)]
      {:db       (p/assoc-if (dissoc db ::description-of-work) ::description-of-work value)
       :dispatch [::routing/merge-query-params {:description-of-work value} :route/replace]})))

(rf/reg-event-db ::restore-from-query
  (fn [db [_ query]]
    (-> db
        (update ::project-name #(or % (:project-name query)))
        (update ::description-of-work #(or % (:description-of-work query))))))

; Documents uploaded to be analyzed with AI to help prefill application info.
(rf/reg-sub ::uploaded-documents :-> ::uploaded-documents)

;; The in-flight analysis request is tracked in app-db as {:id ... :abort-controller ...}.
;; Storing the native AbortController here (rather than at the comm boundary) is a deliberate
;; simplicity tradeoff: there is only ever one analysis in flight and it is transient wizard state.
(defn- active-analysis-request? [db request-id]
  (= request-id (get-in db [::analysis-request :id])))

(defn- active-abort-controller [db]
  (get-in db [::analysis-request :abort-controller]))

(rf/reg-event-db ::add-uploaded-document
  (fn [db [_ {:keys [file-id filename file-mime-type]}]]
    (update db ::uploaded-documents (fnil conj []) {:file-id      file-id
                                                    :filename     filename
                                                    :content-type file-mime-type})))

(rf/reg-event-db ::remove-uploaded-document
  (fn [db [_ file-id]]
    (update db ::uploaded-documents (fn [docs] (filterv #(not= (:file-id %) file-id) docs)))))

(rf/reg-event-fx ::cancel-upload
  (fn [{:keys [db]} _]
    {:db       (dissoc db ::uploaded-documents)
     :dispatch [::upload/cancel-upload]}))

(rf/reg-sub ::langfuse-session-id :-> ::langfuse-session-id)

(rf/reg-event-fx ::cleanup
  (fn [{:keys [db]} _]
    (p/assoc-if {:db       (dissoc db ::project-id ::project-name ::description-of-work
                                   ::uploaded-documents ::analysis-loading? ::analysis-request
                                   ::pending-projects? ::analysis-succeeded? ::langfuse-session-id)
                 :dispatch [::upload/cancel-upload]}
                :comm/abort (active-abort-controller db))))

(rf/reg-event-fx ::analyze-documents
  (fn [{:keys [db]} [_ permit-type domain-group-id]]
    (let [documents        (::uploaded-documents db)
          session-id       (or (::langfuse-session-id db) (str (random-uuid)))
          request-id       (random-uuid)
          abort-controller (js/AbortController.)]
      (p/assoc-if
        {:db           (assoc db
                              ::analysis-loading? true
                              ::analysis-request {:id                request-id
                                                  :abort-controller  abort-controller
                                                  :document-file-ids (set (map :file-id documents))}
                              ::langfuse-session-id session-id)
         :comm/command {:command          :workspace/pre-draft-analysis
                        :signal           (.-signal abort-controller)
                        :data             (p/assoc-if
                                            {:file-ids            (mapv #(select-keys % [:file-id :content-type]) documents)
                                             :permit-type         permit-type
                                             :group-id            domain-group-id
                                             :langfuse-session-id session-id}
                                            :authority-group-id (:db/id (subs/authority-group db)))
                        :on-success       [::analyze-documents.success request-id]
                        :finally-dispatch [::analyze-documents.finally request-id]}}
        ;; abort any request that was still in flight before starting a new one
        :comm/abort (active-abort-controller db)))))

(rf/reg-event-fx ::cancel-analysis
  (fn [{:keys [db]} _]
    (p/assoc-if {:db (cond-> (-> db
                                 (assoc ::analysis-loading? false)
                                 (dissoc ::analysis-request))
                       ;; don't remove langfuse-session-id if there is another already completed analysis
                       (not (::analysis-succeeded? db))
                       (dissoc ::langfuse-session-id))}
                :comm/abort (active-abort-controller db))))

(rf/reg-event-fx ::analyze-documents.success
  (fn [{:keys [db]} [_ request-id _ {:keys [permit-type-sequence project-name description location]}]]
    (if (active-analysis-request? db request-id)
      (let [match               (:route/match db)
            workspace           (when-let [wid (subs/workspace-id match)]
                                  (entities/find-entity (entities/db->context db) {:workspace/id wid}))
            analyzed-file-ids   (get-in db [::analysis-request :document-file-ids])
            classification      (when (seq permit-type-sequence)
                                  (let [[category work-type work-target] (map :id permit-type-sequence)]
                                    (p/assoc-if {:category category}
                                                :work-types (when work-type [work-type])
                                                :work-target work-target)))
            set-project-name?   (and project-name
                                     (not (project-model/permit-types-without-project
                                            (subs/permit-type match workspace)))
                                     (str/blank? (::project-name db)))
            set-description?    (and description (str/blank? (::description-of-work db)))
            set-classification? (and classification (nil? (::classification/category-id db)))
            query-params        (merge
                                  (p/assoc-if {}
                                              :project-name (when set-project-name? project-name)
                                              :description-of-work (when set-description? description))
                                  (when set-classification?
                                    {:category    (:category classification)
                                     :work-types  (:work-types classification)
                                     :work-target (:work-target classification)}))]
        {:db (-> db
                 (assoc ::analysis-succeeded? true)
                 (update ::uploaded-documents
                         (fn [documents]
                           (mapv #(assoc % :analyzed? (contains? analyzed-file-ids (:file-id %)))
                                 documents))))
         :fx (cond-> []
               set-project-name?
               (conj [:dispatch [::set-project-name project-name]])
               set-description?
               (conj [:dispatch [::set-description-of-work description]])
               set-classification?
               (conj [:dispatch [::classification/set-classification classification]])
               (seq query-params)
               (conj [:dispatch [::routing/merge-query-params query-params :route/replace]])
               location
               (conj [:dispatch [::search-analysis-location location]]))})
      {})))

;; Dispatched after the analysed classification is set, so the location setting of that classification applies
(rf/reg-event-fx ::search-analysis-location
  (fn [{:keys [db]} [_ location]]
    (when-not (classification/location-not-required? (entities/db->context db)
                                                     (::classification/category-id db)
                                                     (::classification/work-type-ids db)
                                                     (::classification/work-target-id db))
      {:fx [[:dispatch [::location/search-and-select-location location]]]})))

(rf/reg-event-db ::analyze-documents.finally
  (fn [db [_ request-id]]
    (if (active-analysis-request? db request-id)
      (-> db
          (assoc ::analysis-loading? false)
          (dissoc ::analysis-request))
      db)))

(rf/reg-sub ::analysis-loading?
  (fn [db _] (boolean (::analysis-loading? db))))

(rf/reg-sub ::analysis-succeeded?
  (fn [db _] (boolean (::analysis-succeeded? db))))

(rf/reg-sub ::analysis-completed?
  :<- [::analysis-succeeded?]
  :<- [::subs/workspace]
  (fn [[analysis-succeeded? workspace] _]
    (or analysis-succeeded?
        (:workspace/wizard-ai-session-id workspace))))

(rf/reg-sub ::step-completed?
  :<- [::project-id]
  :<- [::project-name]
  :<- [::description-of-work]
  :<- [::authority-create/authority-create-mode-ok?]
  :<- [::subs/permit-type]
  (fn [[project-id project-name description-of-work authority-create-mode-ok? permit-type] _]
    (boolean
      (and (or project-id
               (str-util/blank->nil project-name)
               (project-model/permit-types-without-project permit-type))
           (str-util/blank->nil description-of-work)
           authority-create-mode-ok?))))

;; Views

(defn- uploaded-document-row [tr removal-disabled? {:keys [file-id filename analyzed?]}]
  [:div.py-3.flex.items-center.gap-3.border-b.border-solid.border-gray-200.last:border-b-0
   [:div.h-10.w-10.shrink-0.flex.items-center.justify-center.rounded.bg-ui-violet-10
    (cc/icon {:icon        i/document
              :height      "20"
              :width       "20"
              :description ""
              :aria-hidden true})]
   [:div.flex-1.min-w-0.text-sm.break-all filename]
   (when analyzed?
     [:span.shrink-0.rounded-lg.bg-ui-violet-10.p-2.text-sm.font-semibold.text-ui-violet-80
      (tr [:application-wizard/plan-document-analyzed])])
   [ui-ib/icon-button {:icon        i/remove
                       :description (tr [:application-wizard/plan-remove-document])
                       :disabled?   removal-disabled?
                       :on-click    #(>evt [::remove-uploaded-document file-id])}]])

(defn- uploaded-documents-section [tr documents permit-type domain-group-id
                                   analysis-loading? any-upload-pending?]
  [:div.rounded.border.border-solid.border-gray-400.mb-6.p-6
   [:div.flex.flex-col.md:flex-row.md:items-center.md:justify-between.gap-4.mb-4
    [:span.text-lg.font-semibold
     (tr [:application-wizard/plan-uploaded-documents] [(count documents)])]
    (if analysis-loading?
      [:div.flex.flex-col.md:flex-row.md:items-center.gap-3
       (cc/inline-loading {:description (tr [:application-wizard/plan-document-upload-analyzing])})
       [ui/button {:kind     :ghost
                   :on-click #(>evt [::cancel-analysis])}
        (tr [:application-wizard/plan-cancel-document-analysis])]]
      [:div.flex.flex-col.md:flex-row.md:items-center.gap-3
       [ui/button {:kind     :ghost
                   :size     :small
                   :on-click #(>evt [::cancel-upload])}
        (tr [:application-wizard/plan-cancel-upload])]
       [ui/button {:kind     :secondary
                   :size     :small
                   :disabled (or (empty? documents) any-upload-pending?)
                   :on-click #(>evt [::analyze-documents permit-type domain-group-id])}
        (tr [:application-wizard/plan-analyze-documents])]])]
   (futil/for-frag [doc documents]
     [uploaded-document-row tr analysis-loading? doc])])

(defn document-upload-area
  "PDF upload area for pre-draft document analysis. Supports file selection and drag-and-drop."
  [tr analysis-loading?]
  [upload/upload-area "plan-document-upload"
   {:accept-types      ["application/pdf" ".pdf"]
    :attach-file-v2-fn #(>evt [::add-uploaded-document %])
    :disabled?         analysis-loading?
    :hide-styles       true

    :additional-content
    [:div.upload-area__drop-area.rounded.px-4.py-6.mb-6.flex.flex-col.items-center.text-center
     (cc/icon {:icon        i/document-search
               :class-name  "mb-4"
               :height      "70"
               :width       "70"
               :description ""
               :aria-hidden true})
     [:h3.text-xl.font-semibold.mb-2 (tr [:application-wizard/plan-document-upload-title])]
     [:p.max-w-3xl (tr [:application-wizard/plan-document-upload-description])]
     [:p.font-bold (tr [:application-wizard/plan-upload-documents-dnd])]
     [upload/upload-button "plan-document-upload"
      {:label-text        (tr [:application-wizard/plan-upload-documents])
       :attach-file-v2-fn #(>evt [::add-uploaded-document %])
       :disabled?         analysis-loading?
       :multiple          true
       :accept-types      ["application/pdf" ".pdf"]
       :button-opts       {:kind       :tertiary
                           :icon       i/upload
                           :class-name "max-w-none"}}]]}])

(defn- document-upload-section [tr]
  (let [permit-type         (<sub [::subs/permit-type])
        domain-group-id     (<sub [::subs/domain-group-id])
        documents           (<sub [::uploaded-documents])
        analysis-loading?   (<sub [::analysis-loading?])
        any-upload-pending? (not (empty? (<sub [::upload/pending-files "plan-document-upload"])))]
    [:div.mb-6
     {:data-guide-id          "document-upload"
      :data-guide-label       (tr [:application-wizard/plan-upload-documents])
      :data-guide-description "Upload PDF documents for AI-powered pre-draft analysis and auto-fill"}
     [document-upload-area tr (or analysis-loading? any-upload-pending?)]
     (when (seq documents)
       [uploaded-documents-section tr documents permit-type domain-group-id
        analysis-loading? any-upload-pending?])]))

(defn- description-of-work-text-area [tr id]
  [:div {:data-guide-id                 "description-of-work"
         :data-guide-label              (tr [:application-wizard/plan-description-of-work])
         :data-guide-description        "Text area to enter a description of the work being done in this application"
         :data-guide-highlight-selector "textarea"}
   [ui/text-area {:id          id
                  :light       true
                  :class       "my-5"
                  :input-class "rounded border border-solid border-gray-400"
                  :label-text  (tr [:application-wizard/plan-description-of-work])
                  :required    true
                  :value       (<sub [::description-of-work])
                  :placeholder (tr [:application-wizard/plan-description-of-work-placeholder])
                  :on-change   #(>evt [::set-description-of-work-from-ui (futil/tval %)])}]])

(defn- create-new-project-tab [tr]
  [:div.mt-3
   [:div {:data-guide-id                 "new-project-name"
          :data-guide-label              (tr [:application-wizard/plan-project-name])
          :data-guide-description        "Enter the name for the new project"
          :data-guide-highlight-selector "input"}
    [ui/text-input {:id          "wiz-plan-project-name"
                    :light       true
                    :class       "my-5"
                    :input-class "rounded border border-solid border-gray-400"
                    :label-text  (tr [:application-wizard/plan-project-name])
                    :required    true
                    :value       (<sub [::project-name])
                    :placeholder (tr [:application-wizard/plan-project-name-placeholder])
                    :on-change   #(>evt [::set-project-name-from-ui (futil/tval %)])}]]
   [description-of-work-text-area tr "wiz-plan-description-of-work-new"]])

(rc/reg-chain ::load-user-projects
  (fn [{:keys [db]} _]
    {:db           (assoc db ::pending-projects? true)
     :comm/command {:command :project/find}})
  (fn [{:keys [db]} [_ status response]]
    {:db       (assoc db ::pending-projects? false)
     :dispatch [::entities/merge-entity-db status response]}))

(rf/reg-sub ::pending-projects? :-> ::pending-projects?)

(rf/reg-sub ::municipality
  :<- [:route/match]
  (fn [match _]
    (get-in match [:parameters :query :municipality])))

(rf/reg-sub ::projects
  :<- [::entities/entities {:entity/type :entity.type/project}]
  :<- [::municipality]
  (fn [[projects municipality] _]
    (->> projects
         (filter #(= municipality (-> % :project/municipality :municipality/number)))
         (filter (complement (du/in-state-fn :project.state/closed)))
         (filter #(-> % :granted-permissions :role.project/writer)))))

(defn- existing-project-tab [tr]
  (let [pending? (<sub [::pending-projects?])
        projects (<sub [::projects])
        selected (<sub [::project-id])
        lang     (<sub [:language])]
    (if pending?
      [:div.mt-3 (cc/skeleton-text {})]
      [:div
        ; It is impossible to style tile-group as a two column grid by passing it options. This scoped css does it.
       [:style "
          @media (min-width: 640px) {
            #existing-projects-tile-group fieldset > div {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 0.75rem;
            }
          }
          #existing-projects-tile-group label.bx--tile.bx--tile--is-selected {
            outline: 2px solid #4f0890;
            background-color: #ede5f5;
          }
          #existing-projects-tile-group label.bx--tile.bx--tile--selectable:hover {
            background-color: #ede5f5;
          }"]
       [:div {:id                     "existing-projects-tile-group"
              :data-guide-id          "existing-projects"
              :data-guide-label       (tr [:application-wizard/plan-existing-project-tab] [(str (count projects))])
              :data-guide-description "Select an existing project from the list"}
        (into
          (cc/tile-group
            {:name           "project-selection"
             :value-selected (or selected "")
             :on-change      #(>evt [::select-project-from-ui %])
             :class-name     "mt-3"})
          (futil/for-all [{:keys [:db/id] :project/keys [name structural-address municipality property]} projects]
            (cc/radio-tile
              {:key        id
               :value      id
               :class-name "rounded mb-3 sm:mb-0 border border-solid border-gray-400"}
              [:div.flex.items-start.gap-3
               (cc/icon {:icon        i/document-list
                         :description (str name)})
               [:div
                [:div.font-bold.mb-2 name]
                [:div.text-sm (str (or (:address/full-address structural-address)
                                       (:property/id property))
                                   ", "
                                   (municipality-model/common-name-province municipality tr lang))]]])))]
       [description-of-work-text-area tr "wiz-plan-description-of-work-existing"]])))

(defn selected-tab-id [project-id]
  (when project-id :existing))

(defn- project-selection-section [tr document-upload-visible?]
  (let [projects (<sub [::projects])]
    [:div.border-solid.border-gray-400
     {:class                  (when document-upload-visible? "border-t")
      :data-guide-id          "project-selection"
      :data-guide-label       (tr [:wizard/project-heading])
      :data-guide-description "Choose an existing project or create a new one, and describe the work"}
     [ui-tabs/tabs
      {:tab-content-class "px-0 py-1"
       :selected-id       (selected-tab-id (<sub [::project-id]))
       :on-change         (fn [id]
                            (when (= id :create-new)
                              (>evt [::clear-selected-project-from-ui])))
       :tabs              (cond-> [{:id      :create-new
                                    :label   (tr [:application-wizard/plan-create-new-project])
                                    :content [create-new-project-tab tr]}]
                            (seq projects)
                            (conj {:id      :existing
                                   :label   (tr [:application-wizard/plan-existing-project-tab] [(str (count projects))])
                                   :content [existing-project-tab tr]}))}]]))

(defn view [tr]
  (let [document-upload-visible? (<sub [::subs/auto-fill-enabled-for-group?])
        authority?               (<sub [::subs/authority-user?])
        workspace-exists?        (<sub [::subs/workspace-id])
        permit-type              (<sub [::subs/permit-type])
        show-project?            (not (project-model/permit-types-without-project permit-type))]
    [:div#application-wizard--plan
     {:data-guide-page-description
      (str "Application wizard — Plan step: "
           (if show-project?
             "Choose an existing project or create a new one, and describe the work."
             "Describe the application.")
           (when document-upload-visible?
             " You can also upload documents for AI-powered pre-draft analysis."))}
     (when document-upload-visible?
       [document-upload-section tr])
     (if show-project?
       [project-selection-section tr document-upload-visible?]
       [description-of-work-text-area tr "wiz-plan-description-of-work"])
     (when (and authority? (not workspace-exists?))
       [authority-create/selector tr])]))
